/**
 * Probes: when a blind bot should scout instead of attack, and the survey
 * shot that flies past the most unexplored planets.
 */

import type { PlayerState, GameState, BotConfig, BotFogContext } from '../../types';
import type { BotFireResult } from './types';
import { vecLength, vecSub, vecNormalize, vecScale, vecAdd } from '../../math';
import { PHYS_DT, OUT_OF_BOUNDS } from '../../constants';
import { computeLaunchDirection } from '../../core/gameState';
import { rk4Step, checkCollision, megaPlanetPowerMult } from '../../core/physics';
import { getSurfaceRadius } from '../../core/surface';
import type { SeededRandom } from '../../core/random';
import { hasAmmo } from './common';
import { tuningFor } from './tuning';
import { freshTracks, probeValue } from '../../core/awareness';
import { WEAPONS } from '../../constants';

/**
 * Decide if bot should fire a scout probe instead of attacking.
 * Enhanced logic: considers unprobed planets and probe attempt count.
 *
 * Rules:
 * 1. Never probe when enemies are visible
 * 2. If ghosts exist but significant unprobed planets remain AND < 3 probe attempts → re-probe
 * 3. If truly blind AND >= 4 probe attempts → stop probing (switch to random fire)
 * 4. Otherwise: probe when blind (no ghosts, no visible enemies)
 */
export function shouldScoutProbe(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  fogContext?: BotFogContext
): boolean {
  if (!fogContext) return false;
  if (fogContext.visibleEnemyIds.length > 0) return false;
  // I3: a probe flies on its own rounds or, the cheapest drone, on a carried drone (core/actions probePaidByDrone)
  if (!hasAmmo(player, 'probe') && (player.drones ?? 0) <= 0) return false;
  const t = tuningFor(config).scouting;

  const probeAttempts = fogContext.probeAttempts ?? 0;

  // Phase 58 — is it worth a turn to find out? The most informative planet's probe value: stale tracks worth
  // refreshing before a shot is spent on them, plus enemies the team cannot place on planets it has not looked at
  if (t.useAwareness && fogContext.awareness) {
    const aw = fogContext.awareness;
    const turn = aw.turn;
    if (freshTracks(aw, turn, 0).some(tr => tr.unitId !== undefined)) return false;   // something in sight now
    let best = 0;
    for (let i = 0; i < gameState.planets.length; i++) {
      const planet = gameState.planets[i];
      if (!planet.alive || planet.type !== 'planet' || i === player.planetIndex) continue;
      best = Math.max(best, probeValue(aw, i, turn, planet.radius, { effectiveRadius: WEAPONS.standard.blastRadius * 2.2 }));
    }
    if (best < t.probeValueThreshold) return false;                                    // a probe would not change a shot
    const anyTrack = aw.tracks.length > 0;
    return probeAttempts < (anyTrack ? t.maxAttemptsWithGhosts : t.maxAttemptsBlind);
  }

  const hasGhosts = fogContext.ghosts.length > 0;

  // Check if significant unprobed planets exist
  let hasUnprobedPlanets = false;
  if (fogContext.planetRevealFractions) {
    for (let i = 0; i < fogContext.planetRevealFractions.length; i++) {
      const planet = gameState.planets[i];
      if (!planet.alive || planet.type !== 'planet' || i === player.planetIndex) continue;
      if (fogContext.planetRevealFractions[i] < t.unprobedRevealFraction) {
        hasUnprobedPlanets = true;
        break;
      }
    }
  }

  if (hasGhosts) {
    // Have ghost intel but no direct LOS — probe more aggressively to verify intel
    if (hasUnprobedPlanets) return probeAttempts < t.maxAttemptsWithGhosts;
    // All planets probed and have ghost intel — fire at ghosts, don't re-probe
    return false;
  }

  // Truly blind — stop probing after 5 attempts (switch to random fire)
  if (probeAttempts >= t.maxAttemptsBlind) return false;

  return true;
}

/**
 * Compute a "survey shot" probe that maximizes planet flybys.
 * Simulates multiple trajectories and picks the one that passes near
 * the most distinct planets (excluding the bot's own planet).
 */
export function computeScoutProbe(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  fogContext: BotFogContext
): BotFireResult | null {
  const t = tuningFor(config).scouting;
  const planet = gameState.planets[player.planetIndex];
  const normal = vecNormalize(player.surfaceDir);
  const surfaceRadius = getSurfaceRadius(planet, player.surfaceDir);
  const worldPos = vecAdd(planet.position, vecScale(normal, surfaceRadius));
  const startPos = vecAdd(worldPos, vecScale(normal, 1.2));

  // Survey radius: how close the probe needs to pass to "survey" a planet
  const SURVEY_RADIUS = t.surveyRadius;
  const maxSteps = t.maxSteps; // default 7200 = 60 seconds, a full orbit
  // Grace period: ignore own-planet collisions in the first 600 steps (5 sec)
  // so the probe can escape its own gravity well
  const SELF_HIT_GRACE = t.selfHitGraceSteps;

  // Other planets to survey
  const otherPlanets = gameState.planets
    .map((p, i) => ({ planet: p, index: i }))
    .filter(({ planet, index }) =>
      planet.alive && index !== player.planetIndex && planet.type !== 'wormhole'
    );
  if (otherPlanets.length === 0) return null;

  let bestAz = 0;
  let bestEl = 0.8;
  let bestPow = 50;
  let bestSurveyScore = -1;

  // Grid search: 20 azimuths × 10 elevations × 5 powers = 1000 sims
  const azSteps = t.azimuthSteps;
  const elSteps = t.elevations;
  const powSteps = t.powers;

  for (let ai = 0; ai < azSteps; ai++) {
    const az = (ai / azSteps) * Math.PI * 2;

    for (const el of elSteps) {
      for (const pow of powSteps) {
        const launchDir = computeLaunchDirection(normal, az, el);
        let pos = { ...startPos };
        let vel = vecScale(launchDir, pow * megaPlanetPowerMult(planet.radius));

        const surveyed = new Set<number>();
        let flightSteps = 0;

        for (let step = 0; step < maxSteps; step++) {
          const result = rk4Step(pos, vel, PHYS_DT, gameState.planets);
          pos = result.pos;
          vel = result.vel;
          flightSteps = step;

          // Check planet collisions
          const collision = checkCollision(pos, gameState.planets);
          if (collision) {
            // Early self-hit (within grace period) = abort, bad trajectory
            if (collision.planetIndex === player.planetIndex && step < SELF_HIT_GRACE) break;
            // Late self-hit = probe did its job and came home, stop sim
            if (collision.planetIndex === player.planetIndex) break;
            // Hit another planet = count as surveyed, stop sim
            surveyed.add(collision.planetIndex);
            break;
          }
          if (vecLength(pos) > OUT_OF_BOUNDS) break;

          // Check proximity to other planets (every 6th step for perf)
          if (step % 6 === 0) {
            for (const { planet: p, index } of otherPlanets) {
              if (surveyed.has(index)) continue;
              const dist = vecLength(vecSub(pos, p.position));
              if (dist < p.radius + SURVEY_RADIUS) {
                surveyed.add(index);
              }
            }
          }
        }

        // Score: planets surveyed weighted by how unprobed they are + flight tiebreaker
        let score = 0;
        for (const idx of surveyed) {
          const revealFrac = fogContext?.planetRevealFractions?.[idx] ?? 0;
          score += (1 - revealFrac) * t.unprobedWeight; // unprobed planets worth more
          if (t.useAwareness && fogContext?.awareness) {
            // what this planet would tell the team (stale tracks to refresh, enemies that could be there)
            score += probeValue(fogContext.awareness, idx, fogContext.awareness.turn, gameState.planets[idx].radius, { effectiveRadius: WEAPONS.standard.blastRadius * 2.2 }) * t.staleWeight;
          }
        }
        score += flightSteps * t.flightTiebreak; // small tiebreaker for longer flights
        if (score > bestSurveyScore) {
          bestSurveyScore = score;
          bestAz = az;
          bestEl = el;
          bestPow = pow;
        }
      }
    }
  }

  // Add slight scatter so repeated probes cover different paths
  return {
    azimuth: bestAz + (rng.next() - 0.5) * t.scatterAzimuth,
    elevation: Math.max(0.3, Math.min(1.3, bestEl + (rng.next() - 0.5) * t.scatterElevation)),
    power: Math.max(10, Math.min(90, bestPow + (rng.next() - 0.5) * t.scatterPower)),
    weaponKey: 'probe',
  };
}
